<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useElementSize } from '@vueuse/core';
import { formatTime } from '@chatwoot/utils';
import {
  niceMax,
  linePath,
  bandPath,
  gridValues,
  formatDay,
  axisStride,
} from '../../helpers/operationsChartHelper';

const props = defineProps({
  points: {
    type: Array,
    default: () => [],
  },
});

const { t, locale } = useI18n();

const plotRef = ref(null);
const { width } = useElementSize(plotRef);

const HEIGHT = 224;
const PADDING = { top: 14, right: 16, bottom: 26, left: 78 };

const chartWidth = computed(() => Math.max(width.value, 280));
const innerWidth = computed(
  () => chartWidth.value - PADDING.left - PADDING.right
);
const innerHeight = HEIGHT - PADDING.top - PADDING.bottom;

const x = index =>
  PADDING.left +
  (innerWidth.value * index) / Math.max(props.points.length - 1, 1);

const measured = computed(() =>
  props.points.filter(point => Number.isFinite(point.p50))
);

const max = computed(() =>
  niceMax(Math.max(1, ...measured.value.map(point => point.p90)))
);

const y = value =>
  PADDING.top + innerHeight - (innerHeight * value) / max.value;

const grid = computed(() =>
  gridValues(max.value).map(value => ({
    label: formatTime(Math.round(value)),
    y: y(value),
  }))
);

// A day nobody answered on has no median to draw. Rather than closing the gap - which would slide
// every later day to the left and lie about the dates - the line breaks into runs of measured days.
const runs = computed(() => {
  const result = [];
  let current = [];

  props.points.forEach((point, index) => {
    if (Number.isFinite(point.p50)) {
      current.push({ ...point, index });
      return;
    }
    if (current.length) result.push(current);
    current = [];
  });
  if (current.length) result.push(current);

  return result.map(run => ({
    key: run[0].timestamp,
    upper: run.map(point => [x(point.index), y(point.p90)]),
    lower: run.map(point => [x(point.index), y(point.p50)]),
  }));
});

const axisLabels = computed(() =>
  props.points
    .map((point, index) => ({ point, index }))
    .filter(({ index }) => index % axisStride(props.points.length) === 0)
    .map(({ point, index }) => ({
      x: x(index),
      label: formatDay(point.timestamp, locale.value, {
        day: 'numeric',
        month: 'short',
      }),
    }))
);

const hoverBands = computed(() => {
  const bandWidth = innerWidth.value / Math.max(props.points.length - 1, 1);
  return props.points.map((point, index) => ({
    key: point.timestamp,
    x: x(index) - bandWidth / 2,
    width: bandWidth,
    tip: [
      formatDay(point.timestamp, locale.value, {
        weekday: 'short',
        day: 'numeric',
        month: 'short',
      }),
      ...(Number.isFinite(point.p50)
        ? [
            `${t('OPERATIONS_REPORTS.FIRST_RESPONSE.MEDIAN')}: ${formatTime(point.p50)}`,
            `${t('OPERATIONS_REPORTS.FIRST_RESPONSE.P90')}: ${formatTime(point.p90)}`,
            t('OPERATIONS_REPORTS.FIRST_RESPONSE.SAMPLE', {
              count: point.count,
            }),
          ]
        : [t('OPERATIONS_REPORTS.FIRST_RESPONSE.NO_ANSWERS')]),
    ].join('\n'),
  }));
});
</script>

<template>
  <div
    class="flex flex-col p-4 border rounded-xl border-n-weak bg-n-solid-2 gap-1"
  >
    <div>
      <h3 class="text-sm font-semibold text-n-slate-12">
        {{ t('OPERATIONS_REPORTS.FIRST_RESPONSE.TITLE') }}
      </h3>
      <p class="mt-0.5 mb-0 text-xs text-n-slate-11">
        {{ t('OPERATIONS_REPORTS.FIRST_RESPONSE.SUBTITLE') }}
      </p>
    </div>

    <div class="flex items-center gap-3 mt-2 mb-1 text-xs text-n-slate-11">
      <span class="inline-flex items-center gap-1.5">
        <i class="w-3 h-[3px] rounded-sm bg-n-blue-9" />
        {{ t('OPERATIONS_REPORTS.FIRST_RESPONSE.MEDIAN') }}
      </span>
      <span class="inline-flex items-center gap-1.5">
        <i class="w-3 h-[3px] rounded-sm bg-n-blue-8" />
        {{ t('OPERATIONS_REPORTS.FIRST_RESPONSE.P90') }}
      </span>
    </div>

    <div ref="plotRef" class="w-full">
      <p
        v-if="!measured.length"
        class="py-16 mb-0 text-sm text-center text-n-slate-10"
      >
        {{ t('OPERATIONS_REPORTS.EMPTY') }}
      </p>
      <svg
        v-else
        :viewBox="`0 0 ${chartWidth} ${HEIGHT}`"
        :height="HEIGHT"
        class="block w-full"
        role="img"
        :aria-label="t('OPERATIONS_REPORTS.FIRST_RESPONSE.TITLE')"
      >
        <g v-for="line in grid" :key="`grid-${line.y}`">
          <line
            :x1="PADDING.left"
            :y1="line.y"
            :x2="PADDING.left + innerWidth"
            :y2="line.y"
            class="stroke-n-weak"
            stroke-width="1"
          />
          <text
            :x="PADDING.left - 8"
            :y="line.y + 4"
            text-anchor="end"
            font-size="10"
            class="fill-n-slate-10 tabular-nums"
          >
            {{ line.label }}
          </text>
        </g>

        <text
          v-for="label in axisLabels"
          :key="`axis-${label.x}`"
          :x="label.x"
          :y="HEIGHT - 8"
          text-anchor="middle"
          font-size="10"
          class="fill-n-slate-10"
        >
          {{ label.label }}
        </text>

        <g v-for="run in runs" :key="`run-${run.key}`">
          <path :d="bandPath(run.upper, run.lower)" class="fill-n-blue-4" />
          <path
            :d="linePath(run.upper)"
            fill="none"
            class="stroke-n-blue-8"
            stroke-width="2"
            stroke-linejoin="round"
          />
          <path
            :d="linePath(run.lower)"
            fill="none"
            class="stroke-n-blue-9"
            stroke-width="2"
            stroke-linejoin="round"
          />
        </g>

        <rect
          v-for="band in hoverBands"
          :key="`hover-${band.key}`"
          :x="band.x"
          :y="PADDING.top"
          :width="band.width"
          :height="innerHeight"
          :data-tip="band.tip"
          class="fill-transparent hover:fill-n-alpha-1"
        />
      </svg>
    </div>
  </div>
</template>
